import { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getPriceId as getEnvPriceId } from '../utils/pricingUtils';

// Cache for pricing data (module-level singleton)
let pricingCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fallback hardcoded pricing data (used when Firestore fetch fails)
const FALLBACK_PRICING_CONFIG = {
  tiers: {
    US: ["US", "DE", "UA", "AU", "CA"],
    JP: ["IE", "GB", "FR", "JP", "KR", "IL", "IT"],
    TW: ["TW", "ES", "PT", "PL"],
    PH: ["PH", "MY", "CN", "AR", "BR"],
    TH: ["TH", "VN", "ID", "IN", "NG"]
  },
  prices: {
    US: {
      basic: { monthly: "$3", yearly: "$2.33", yearlyTotal: "$28" },
      solo: { monthly: "$6", yearly: "$4.75", yearlyTotal: "$57" },
      pro: { monthly: "$19", yearly: "$16.58", yearlyTotal: "$199" }
    },
    JP: {
      basic: { monthly: "$2.50", yearly: "$2", yearlyTotal: "$24" },
      solo: { monthly: "$5", yearly: "$4", yearlyTotal: "$48" },
      pro: { monthly: "$16", yearly: "$14", yearlyTotal: "$168" }
    },
    TW: {
      basic: { monthly: "$2", yearly: "$1.58", yearlyTotal: "$19" },
      solo: { monthly: "$4", yearly: "$3.17", yearlyTotal: "$38" },
      pro: { monthly: "$13", yearly: "$10.83", yearlyTotal: "$130" }
    },
    PH: {
      basic: { monthly: "$1.50", yearly: "$1.17", yearlyTotal: "$14" },
      solo: { monthly: "$3", yearly: "$2.33", yearlyTotal: "$28" },
      pro: { monthly: "$9.50", yearly: "$7.58", yearlyTotal: "$91" }
    },
    TH: {
      basic: { monthly: "$1", yearly: "$0.75", yearlyTotal: "$9" },
      solo: { monthly: "$2", yearly: "$1.58", yearlyTotal: "$19" },
      pro: { monthly: "$6", yearly: "$4.75", yearlyTotal: "$57" }
    }
  },
  defaultTier: "US"
};

/**
 * Hook to fetch pricing data from Firestore /config/pricing document
 *
 * @param {string} region - Country code for regional pricing (e.g., "US", "JP", "PH")
 * @returns {Object} { pricing, loading, error, rawProducts }
 *   - pricing: { basic: { monthly: {...}, yearly: {...} }, solo: {...}, pro: {...} }
 *   - loading: boolean
 *   - error: Error object or null
 *   - rawProducts: Raw Firestore pricing config for debugging
 */
export function usePricing(region = 'US') {
  const [pricing, setPricing] = useState(null);
  const [rawProducts, setRawProducts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    async function fetchPricing() {
      try {
        let pricingConfig;

        // Check cache first
        const now = Date.now();
        if (pricingCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
          pricingConfig = pricingCache;
        } else {
          // Fetch from Firestore
          const docRef = doc(db, 'config', 'pricing');
          const snapshot = await getDoc(docRef);

          if (snapshot.exists()) {
            pricingConfig = snapshot.data();
            // Update cache
            pricingCache = pricingConfig;
            cacheTimestamp = now;
          } else {
            console.warn('Pricing config not found in Firestore, using fallback');
            pricingConfig = FALLBACK_PRICING_CONFIG;
          }
        }

        setRawProducts(pricingConfig);

        // Determine pricing tier for the region
        const pricingTier = getPricingTierFromConfig(region, pricingConfig);

        // Build pricing in the format expected by PricingTable
        const pricingData = buildPricingFromConfig(pricingConfig, pricingTier);
        setPricing(pricingData);
        setError(null);
      } catch (err) {
        console.error('Error fetching pricing config:', err);
        setError(err);

        // Fallback to hardcoded pricing
        const pricingTier = getPricingTierFromConfig(region, FALLBACK_PRICING_CONFIG);
        const pricingData = buildPricingFromConfig(FALLBACK_PRICING_CONFIG, pricingTier);
        setPricing(pricingData);
      } finally {
        setLoading(false);
      }
    }

    // Only fetch once per mount (or when region changes)
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchPricing();
    } else {
      // Region changed, rebuild pricing from cached data
      if (pricingCache) {
        const pricingTier = getPricingTierFromConfig(region, pricingCache);
        const pricingData = buildPricingFromConfig(pricingCache, pricingTier);
        setPricing(pricingData);
      }
    }
  }, [region]);

  return { pricing, loading, error, rawProducts };
}

/**
 * Determine the pricing tier for a country code using Firestore config
 * @param {string} countryCode - Two-letter country code (e.g., "US", "PH")
 * @param {Object} config - Pricing config with tiers
 * @returns {string} - Tier key (e.g., "US", "JP", "TW", "PH", "TH")
 */
function getPricingTierFromConfig(countryCode, config) {
  const code = countryCode?.toUpperCase() || 'US';
  const { tiers, defaultTier } = config;

  for (const [tierKey, countries] of Object.entries(tiers)) {
    if (countries.includes(code)) {
      return tierKey;
    }
  }

  return defaultTier || 'US';
}

/**
 * Parse a price string like "$3" or "$2.33" to a number
 * @param {string} priceStr - Price string with $ prefix
 * @returns {number} - Numeric value
 */
function parsePrice(priceStr) {
  if (!priceStr) return 0;
  return parseFloat(priceStr.replace('$', ''));
}

/**
 * Build pricing data in the format expected by PricingTable component
 * @param {Object} config - Pricing config from Firestore
 * @param {string} pricingTier - The pricing tier to use
 * @returns {Object} - Pricing object compatible with PricingTable
 */
function buildPricingFromConfig(config, pricingTier) {
  const tierPrices = config.prices[pricingTier] || config.prices[config.defaultTier] || config.prices.US;

  const buildPlanPricing = (planKey) => {
    const planPrices = tierPrices[planKey];
    if (!planPrices) return null;

    const monthlyAmount = parsePrice(planPrices.monthly);
    const yearlyEquiv = parsePrice(planPrices.yearly);
    const yearlyTotal = parsePrice(planPrices.yearlyTotal);

    return {
      monthly: {
        amount: monthlyAmount,
        displayAmount: planPrices.monthly,
        interval: 'monthly',
        billDesc: `Billed Monthly at ${planPrices.monthly} USD`,
        priceId: getEnvPriceId(planKey, pricingTier, 'monthly')
      },
      yearly: {
        amount: yearlyTotal,
        displayAmount: planPrices.yearly,
        monthlyEquivalent: yearlyEquiv,
        interval: 'yearly',
        billDesc: `Billed Yearly at ${planPrices.yearlyTotal} USD`,
        priceId: getEnvPriceId(planKey, pricingTier, 'yearly')
      }
    };
  };

  return {
    basic: buildPlanPricing('basic'),
    solo: buildPlanPricing('solo'),
    pro: buildPlanPricing('pro')
  };
}

export default usePricing;
